import { useAuth } from '@clerk/react';

import { useUsage } from '@/api/usage';
import { contactCountBucket, messageCountBucket } from '@/lib/analytics';
import type { Plan } from '@/types/permissions';

/**
 * Derives the current plan name from Clerk feature flags.
 *
 * Checks features from highest tier down so the first match wins.
 */
function derivePlan(has: (params: { feature: string }) => boolean): Plan {
  if (has({ feature: 'deep_enrichment' })) return 'nugget';
  if (has({ feature: 'crm_integration' })) return 'vein';
  if (has({ feature: 'unlimited_contacts' })) return 'seam';
  return 'prospect';
}

export interface AnalyticsProps {
  plan: Plan;
  contact_count: string;
  message_count_monthly: string;
}

/**
 * Returns the current user's analytics dimensions as custom property values.
 * Returns null while data is still loading.
 */
export function useAnalyticsProps(): AnalyticsProps | null {
  const { isLoaded, isSignedIn, has } = useAuth();
  const { data: usage } = useUsage({ enabled: isLoaded && !!isSignedIn });

  if (!isLoaded || !has || !usage) return null;

  return {
    plan: derivePlan(has),
    contact_count: contactCountBucket(usage.contacts.count),
    message_count_monthly: messageCountBucket(usage.messages.count),
  };
}
